import { useRef, useState } from "react";
import { FaPlay, FaPause } from "react-icons/fa6";
import { CDN_URL } from "@/consts";
import WaveformPlayer from "@/components/shared/Audio/WaveformPlayer";
import type { ToolContent } from "@/types/tools";

type AudioPreviewProps = {
  audioRef: React.RefObject<HTMLAudioElement>;
  playing: boolean;
  setPlaying: (playing: boolean) => void;
  handlePlay: () => void;
  audioSrc: string;
  title: string;
};

const AudioPreview = ({
  audioRef,
  playing,
  setPlaying,
  handlePlay,
  audioSrc,
  title,
}: AudioPreviewProps) => {
  return (
    <>
      <audio ref={audioRef} src={audioSrc} onEnded={() => setPlaying(false)} className="hidden" />
      <div className="flex w-full flex-1 flex-col items-center gap-2">
        <div className="flex w-full items-center justify-center gap-2">
          <p className="text-gray-600 sm:text-lg">{title}</p>
          <button
            className="h-8 w-8 rounded-full bg-blue-500 px-2 text-white hover:bg-blue-600"
            onClick={handlePlay}
          >
            {playing ? <FaPause className="size-4" /> : <FaPlay className="size-4" />}
          </button>
        </div>

        <WaveformPlayer
          audioRef={audioRef}
          title={title as "Before" | "After"}
          className="h-[150px] w-full rounded-2xl"
        />
      </div>
    </>
  );
};

interface AudioBalancerProps {
  content: ToolContent;
}

const AudioBalancer = ({ content }: AudioBalancerProps) => {
  const beforeAudioRef = useRef<HTMLAudioElement>(null);
  const afterAudioRef = useRef<HTMLAudioElement>(null);
  const [beforePlaying, setBeforePlaying] = useState(false);
  const [afterPlaying, setAfterPlaying] = useState(false);

  const handleBeforePlay = () => {
    if (beforeAudioRef.current) {
      if (beforePlaying) {
        beforeAudioRef.current.pause();
        setBeforePlaying(false);
      } else {
        beforeAudioRef.current.play();
        setBeforePlaying(true);
      }
    }
  };

  const handleAfterPlay = () => {
    if (afterAudioRef.current) {
      if (afterPlaying) {
        afterAudioRef.current.pause();
        setAfterPlaying(false);
      } else {
        afterAudioRef.current.play();
        setAfterPlaying(true);
      }
    }
  };

  return (
    <div className="flex w-full flex-col items-center justify-center gap-4 md:flex-row">
      <AudioPreview
        audioRef={beforeAudioRef}
        playing={beforePlaying}
        setPlaying={setBeforePlaying}
        handlePlay={handleBeforePlay}
        audioSrc={`${CDN_URL}/features/free/audio-balancer/before.mp3`}
        title="Before"
      />
      <AudioPreview
        audioRef={afterAudioRef}
        playing={afterPlaying}
        setPlaying={setAfterPlaying}
        handlePlay={handleAfterPlay}
        audioSrc={`${CDN_URL}/features/free/audio-balancer/after.mp3`}
        title="After"
      />
    </div>
  );
};

export default AudioBalancer;
